// Adjuntos del chat de Amix (D89): que se puede adjuntar y como se achica una imagen antes de subirla (el servicio
// acepta imagenes de hasta 4 MB y el modelo no necesita mas de 2000 px de lado para leer una captura).
export const EXTENSIONES_AMIX = ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'csv', 'tsv', 'json', 'xml', 'md', 'log', 'eml', 'ics', 'html', 'htm', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
export const ACEPTA_AMIX = EXTENSIONES_AMIX.map((e) => `.${e}`).join(',');
export const MAX_ARCHIVO_AMIX = 20 * 1024 * 1024;
export const MAX_IMAGEN_AMIX = 4 * 1024 * 1024;
export const LADO_MAX = 2000;
export const MAX_ADJUNTOS_AMIX = 10;

export const extension = (nombre: string) => (/\.([a-z0-9]+)$/i.exec(nombre)?.[1] ?? '').toLowerCase();
export const esImagenAmix = (nombre: string, tipo = '') => /^image\/(png|jpe?g|gif|webp)$/.test(tipo) || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension(nombre));

// Por que no se puede adjuntar (o null si se puede). Las imagenes grandes no se rechazan: se achican.
export function motivoRechazo(nombre: string, tamano: number, tipo = ''): string | null {
  if (!EXTENSIONES_AMIX.includes(extension(nombre)) && !esImagenAmix(nombre, tipo)) return `${nombre}: Amix lee PDF, Word, Excel, PowerPoint, textos (CSV, TXT…) e imágenes.`;
  if (!tamano) return `${nombre} está vacío.`;
  if (!esImagenAmix(nombre, tipo) && tamano > MAX_ARCHIVO_AMIX) return `${nombre} pesa más de 20 MB.`;
  return null;
}

// Medidas finales de una imagen: el lado mayor en LADO_MAX como mucho, sin agrandar.
export function medidas(ancho: number, alto: number, lado = LADO_MAX) {
  const f = Math.min(1, lado / Math.max(ancho, alto, 1));
  return { ancho: Math.max(1, Math.round(ancho * f)), alto: Math.max(1, Math.round(alto * f)) };
}

// Una captura pegada viene como "image.png": se le pone un nombre con la hora para reconocerla.
export const nombreCaptura = (ahora = new Date()) => `captura-${ahora.toTimeString().slice(0, 8).replaceAll(':', '')}.png`;

// La imagen lista para subir: igual si ya es chica; si no, en JPEG (o PNG si es una captura chica) con el lado mayor
// en 2000 px. GIF animados se dejan igual (si pasan de 4 MB el servicio avisa).
export async function achicarImagen(f: File): Promise<{ datos: Blob; nombre: string }> {
  if (extension(f.name) === 'gif') return { datos: f, nombre: f.name };
  const img = await createImageBitmap(f);
  const { ancho, alto } = medidas(img.width, img.height);
  if (f.size <= MAX_IMAGEN_AMIX && ancho === img.width && alto === img.height) {
    img.close();
    return { datos: f, nombre: f.name };
  }
  const lienzo = document.createElement('canvas');
  lienzo.width = ancho;
  lienzo.height = alto;
  const ctx = lienzo.getContext('2d');
  if (!ctx) throw new Error('No se pudo preparar la imagen.');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, ancho, alto);
  ctx.drawImage(img, 0, 0, ancho, alto);
  img.close();
  const aBlob = (tipo: string, calidad?: number) => new Promise<Blob | null>((ok) => lienzo.toBlob(ok, tipo, calidad));
  let datos = (await aBlob('image/png')) ?? f;
  let ext = 'png';
  for (const calidad of [0.9, 0.8, 0.65, 0.5]) {
    if (datos.size <= MAX_IMAGEN_AMIX) break;
    datos = (await aBlob('image/jpeg', calidad)) ?? datos;
    ext = 'jpg';
  }
  return { datos, nombre: f.name.replace(/\.[a-z0-9]+$/i, '') + `.${ext}` };
}
